import React from 'react'
import styled from 'styled-components'
import { CloseOutlineIcon, TickOutlineIcon } from '@mote/assets/icons'
import { mote_primary } from '@mote/config/defaults/colors'
import { Block, Button, H5, Spinner, Text } from '@mote/ui'
import { Content, DefaultLayout } from '../layouts'

export const DiagnosticsScreen = () => {
  const [loading, setLoading] = React.useState({
    moteLinks: true,
    downloadingAudio: true,
    apiReads: true,
    apiWrites: true
  })
  const [error, setError] = React.useState({
    moteLinks: null,
    downloadingAudio: null,
    apiReads: null,
    apiWrites: null
  })

  const outcome = React.useMemo(() => {
    if (Object.keys(loading).some(key => loading[key])) return null

    const connectionErrors = Object.keys(error).filter(key => error[key])

    if (connectionErrors.length > 0) {
      return (
        <Outcome
          isSuccess={false}
          justify='center'
          align='center'
          gap={10}
          layout='horizontal'
        >
          <Text
            fontSize={18}
            tag='p'
            alignment='center'
            fontWeight={700}
            color='white'
            lineHeight={23}
          >
            {connectionErrors.length} connection issue
            {connectionErrors.length === 1 ? '' : 's'} identified
          </Text>
          <Button
            tag='a'
            variant='link'
            target='_blank'
            href='https://support.mote.com/hc/en-us/articles/360043068831-Help-I-m-seeing-a-page-won-t-load-or-page-blocked-message-'
            text='Learn how to fix now'
          />
        </Outcome>
      )
    }
    return (
      <Outcome
        isSuccess
        justify='center'
        align='center'
        gap={10}
        layout='horizontal'
      >
        <Text
          fontSize={18}
          tag='p'
          alignment='center'
          fontWeight={700}
          color='white'
          lineHeight={23}
        >
          Connections looking good!
        </Text>
      </Outcome>
    )
  }, [loading, error])

  // Check mote.com/m/https://dev-stixzoor.mote.com/m/rkacs2h -> 200
  // Check media.mote.com -> 403
  // Check s.mote.com/c/geo -> 200
  // Check [POST] activity with empty JSON -> 418
  React.useEffect(() => {
    window
      .fetch('https://mote.com/m/rkacs2h')
      .then(response => {
        if (response.status === 200) return setSuccessResponse('moteLinks')

        setErrorResponse('moteLinks')
      })
      .catch(err => setErrorResponse('moteLinks'))

    window
      .fetch('https://media.mote.com/')
      .then(response => {
        if (response.status === 403)
          return setSuccessResponse('downloadingAudio')

        setErrorResponse('downloadingAudio')
      })
      .catch(err => setErrorResponse('downloadingAudio'))

    window
      .fetch('https://s.mote.com/c/geo')
      .then(response => {
        if (response.status === 200) return setSuccessResponse('apiReads')

        setErrorResponse('apiReads')
      })
      .catch(err => setErrorResponse('apiReads'))

    window
      .fetch('https://s.mote.com/a/activity', { method: 'POST' })
      .then(response => {
        if (response.status === 418) return setSuccessResponse('apiWrites')

        setErrorResponse('apiWrites')
      })
      .catch(err => setErrorResponse('apiWrites'))
  }, [])

  function setSuccessResponse(diagnosticName) {
    setLoading(prev => ({ ...prev, [diagnosticName]: false }))
  }

  function setErrorResponse(diagnosticName) {
    setLoading(prev => ({ ...prev, [diagnosticName]: false }))
    setError(prev => ({ ...prev, [diagnosticName]: true }))
  }

  return (
    <Root>
      <Content>
        <Main>
          {outcome}

          <Block gap={22}>
            <H5>Your connection status</H5>

            <div>
              {[
                { key: 'moteLinks', text: 'Mote links' },
                { key: 'downloadingAudio', text: 'Downloading Audio' },
                { key: 'apiReads', text: 'API Reads' },
                { key: 'apiWrites', text: 'API Writes' }
              ].map(identifier => (
                <ConnectionContainer key={identifier.key}>
                  <Block layout='horizontal' gap={8} align='center'>
                    {loading[identifier.key] && (
                      <>
                        <Spinner
                          radius={20}
                          style={{ height: 20 * 1.2 }}
                          color={mote_primary}
                        />
                        <Text tag='p'>Loading...</Text>
                      </>
                    )}

                    {!loading[identifier.key] && !error[identifier.key] && (
                      <>
                        <TickOutlineIcon />
                        <Text tag='p' lineHeight={24}>
                          {identifier.text}
                        </Text>
                      </>
                    )}

                    {!loading[identifier.key] && error[identifier.key] && (
                      <>
                        <CloseOutlineIcon />

                        <Text tag='p'>{identifier.text}</Text>
                      </>
                    )}
                  </Block>
                </ConnectionContainer>
              ))}
            </div>
          </Block>
        </Main>
      </Content>
    </Root>
  )
}

const Root = styled(DefaultLayout)``
const Main = styled(Block)`
  background: #f9f9fb;
  padding: 122px 66px;
`
const Outcome = styled(Block)`
  background: ${({ isSuccess }) => (isSuccess ? '#57C96A' : '#F44336')};
  border-radius: 4px;
  text-align: center;
  padding: 10px;
  margin-bottom: 53px;

  a {
    text-decoration: underline;
    color: white;

    &:hover {
      text-decoration: none;
    }
    span {
      color: white;
    }
  }
`

const ConnectionContainer = styled.div`
  padding: 20px;
  background: white;

  &:not(:last-of-type) {
    border-bottom: 1px solid rgba(213, 212, 218, 0.5);
  }
`
